import { io, Socket } from 'socket.io-client'
import { store } from '../store'
import { updateAssetPrice } from '../store/slices/marketSlice'
import { API_HOST } from '../config/api'

class WebSocketService {
  private socket: Socket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000

  connect() {
    if (this.socket?.connected) {
      console.log('WebSocket already connected')
      return
    }

    console.log('Connecting to WebSocket server...')
    this.socket = io(API_HOST, {
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    })

    this.setupEventListeners()
  }

  private setupEventListeners() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      console.log('WebSocket connected:', this.socket?.id)
      this.reconnectAttempts = 0
    })

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason)
    })

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error)
      this.reconnectAttempts++
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached')
      }
    })

    // 监听价格更新
    this.socket.on('price_update', (data: {
      symbol: string
      current_price: number
      change_24h: number
      change_percent_24h: number
      volume_24h: number
      timestamp: string
    }) => {
      console.log('Price update received:', data)
      
      // 更新Redux store中的价格数据
      store.dispatch(updateAssetPrice({
        symbol: data.symbol,
        price: data.current_price,
        change: data.change_24h,
        volume: data.volume_24h
      }))
    })

    // 监听市场统计更新
    this.socket.on('market_stats', (stats: {
      total_assets: number
      total_volume_24h: number
      total_market_cap: number
      average_change_24h: number
      gainers_count: number
      losers_count: number
      updated_at: string
    }) => {
      console.log('Market stats update:', stats)
      // 可以在这里更新市场统计的Redux state
    })

    // 监听客服消息
    this.socket.on('new_message', (message: {
      id: string
      conversation_id: string
      sender_id: string
      sender_type: 'user' | 'agent'
      content: string
      message_type: string
      created_at: string
    }) => {
      console.log('New chat message received:', message)
      // 触发自定义事件，让聊天组件监听
      window.dispatchEvent(new CustomEvent('newChatMessage', { detail: message }))
    })

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts')
    })
  }

  // 订阅市场数据
  subscribeToMarketData(symbols: string[]) {
    if (!this.socket?.connected) {
      console.warn('WebSocket not connected, cannot subscribe to market data')
      return
    }

    console.log('Subscribing to market data:', symbols)
    this.socket.emit('subscribe_market_data', symbols)
  }

  // 取消订阅市场数据
  unsubscribeFromMarketData(symbols: string[]) {
    if (!this.socket?.connected) {
      console.warn('WebSocket not connected, cannot unsubscribe from market data')
      return
    }

    console.log('Unsubscribing from market data:', symbols)
    this.socket.emit('unsubscribe_market_data', symbols)
  }

  // 断开连接
  disconnect() {
    if (this.socket) {
      console.log('Disconnecting WebSocket...')
      this.socket.disconnect()
      this.socket = null
    }
  }

  // 获取连接状态
  isConnected(): boolean {
    return this.socket?.connected || false
  }

  // 获取Socket实例
  getSocket(): Socket | null {
    return this.socket
  }

  // 加入对话房间
  joinConversation(conversationId: string) {
    if (!this.socket?.connected) {
      console.warn('WebSocket not connected, cannot join conversation')
      return
    }

    console.log('Joining conversation:', conversationId)
    this.socket.emit('join_conversation', conversationId)
  }

  // 离开对话房间
  leaveConversation(conversationId: string) {
    if (!this.socket?.connected) {
      console.warn('WebSocket not connected, cannot leave conversation')
      return
    }

    console.log('Leaving conversation:', conversationId)
    this.socket.emit('leave_conversation', conversationId)
  }
}

// 创建单例实例
export const websocketService = new WebSocketService()
export default websocketService